// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import xngGetQuote from "@conklin/App/Handlers/RequestQuote/xGetQuote";
import xngSubmitQuote from "@conklin/App/Handlers/RequestQuote/xSubmitQuote";
import { RequestQuoteApiParameter } from "@conklin/App/Services/RequestQuoteService";
import { CustomAppState, xRequestQuoteStateType } from "@conklin/App/Store/Reducers";
import { xIsAuthenticated } from "@conklin/App/Store/Selectors/GeneralSelectors";
import Button, { ButtonPresentationProps } from "@conklin/MobiusOverrides/Button/Button";
import { GridContainerProps } from "@conklin/MobiusOverrides/GridContainer/GridContainer";
import Radio, { RadioPresentationProps } from "@conklin/MobiusOverrides/Radio/Radio";
import RadioGroup, { RadioGroupPresentationProps } from "@conklin/MobiusOverrides/RadioGroup/RadioGroup";
import Select, { SelectPresentationProps } from "@conklin/MobiusOverrides/Select";
import TextArea, { TextAreaPresentationProps } from "@conklin/MobiusOverrides/TextArea/TextArea";
import TextField, { TextFieldPresentationProps } from "@conklin/MobiusOverrides/TextField";
import Tooltip, { TooltipPresentationProps } from "@conklin/MobiusOverrides/Tooltip";
import { mergeWithCss, xResetFormFieldError, xValidateOnSubmit } from "@conklin/XDL/Utilities";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import addToWishList, {
    AddToWishListParameter,
} from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import loadWishListIfNeeded from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/LoadWishListIfNeeded";
import translate from "@insite/client-framework/Translate";
import { WishListModel } from "@insite/client-framework/Types/ApiModels";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

interface RFQRequestModalStyles {
    modalStyle?: ModalPresentationProps;
    requiredInformationText?: TypographyPresentationProps;
    formGridContainer?: GridContainerProps;

    firstNameGridItem?: GridItemProps;
    lastNameGridItem?: GridItemProps;
    emailGridItem?: GridItemProps;
    phoneGridItem?: GridItemProps;
    companyNameGridItem?: GridItemProps;
    addressGridItem?: GridItemProps;
    cityGridItem?: GridItemProps;
    stateGridItem?: GridItemProps;
    zipCodeGridItem?: GridItemProps;

    firstNameTextField?: TextFieldPresentationProps;
    lastNameTextField?: TextFieldPresentationProps;
    emailTextField?: TextFieldPresentationProps;
    phoneTextField?: TextFieldPresentationProps;
    companyNameTextField?: TextFieldPresentationProps;
    addressTextField?: TextFieldPresentationProps;
    cityTextField?: TextFieldPresentationProps;
    stateSelect?: SelectPresentationProps;
    zipCodeTextField?: TextFieldPresentationProps;

    enableTaxExemptWrapper?: InjectableCss;
    enableTaxExemptTextWrapper?: InjectableCss;
    enableTaxExemptText?: TypographyPresentationProps;
    enableTaxExemptRadioGroup?: RadioGroupPresentationProps;
    yesOptionRadio?: RadioPresentationProps;
    noOptionRadio?: RadioPresentationProps;
    enableTaxExemptTooltip?: TooltipPresentationProps;

    textExemptTextFieldWrapper?: InjectableCss;
    textExemptTextField?: TextFieldPresentationProps;

    additionalCommentsWrapper?: InjectableCss;
    additionalCommentsTextArea?: TextAreaPresentationProps;

    buttonsWrapper?: InjectableCss;
    cancelButton?: ButtonPresentationProps;
    submitButton?: ButtonPresentationProps;
}

export const rfqRequestModalStyles: RFQRequestModalStyles = {};

const mapStateToProps = (state: CustomAppState) => {
    const cartState = getCurrentCartState(state);
    const cart = cartState.value;
    const isAuthenticated = xIsAuthenticated(state);
    const countries = getCurrentCountries(state);
    const requestQuoteData = state.xRequestQuote;

    return {
        cartId: cart?.id,
        isAuthenticated,
        countries,
        requestQuoteData,
    };
};

const mapDispatchToProps = {
    loadWishListIfNeeded,
    addToWishList: makeHandlerChainAwaitable<AddToWishListParameter, WishListModel>(addToWishList),
    xngGetQuote,
    xngSubmitQuote,
};

interface OwnProps {
    productId: string;
    isModalOpened: boolean;
    handleCloseModal: () => void;
    extendedStyles?: RFQRequestModalStyles;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const StyledForm = getStyledWrapper("form");
const formId = "RFQRequestQuoteForm";

const RFQRequestModal = ({
    productId,
    requestQuoteData,
    isModalOpened,
    isAuthenticated,
    cartId,
    countries,
    extendedStyles,
    handleCloseModal,
    xngGetQuote,
    xngSubmitQuote,
}: Props) => {
    const [styles] = React.useState(() => mergeWithCss(rfqRequestModalStyles, extendedStyles));
    const toasterContext = useContext(ToasterContext);
    const [isInProgress, setIsInProgress] = React.useState(false);
    const [showError, setShowError] = React.useState(false);
    const [rfqBillTo, setRfqBillTo] = React.useState<xRequestQuoteStateType>({
        quoteId: "",
        productId,
        customerFirstName: "",
        customerLastName: "",
        customerEmailAddress: "",
        customerPhoneNumber: "",
        customerCompanyName: "",
        customerAddressLine1: "",
        customerAddressLine2: "",
        customerCity: "",
        customerState: "",
        customerZip: "",
        taxExempt: false,
        taxExemptCertificateNumber: "",
        orderNote: "",
    });
    const [country, setCountry] = useState(countries?.[0]);

    const stateErrorMessage = translate("State is required");
    const taxExemptCertificateLabel = translate("Tax Exempt Certificate Number*");

    const handleBillToInputChange = (
        event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
    ) => {
        const { name, value } = event.currentTarget;

        setRfqBillTo(prev => ({
            ...prev,
            [name]: value,
        }));
    };

    const submitFeedbackClickHandler = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setShowError(true);

        const errors = xValidateOnSubmit(formId);

        if ((errors && errors.length > 0) || !rfqBillTo.customerState) {
            return;
        }

        setIsInProgress(true);

        const requestQuoteApiParameter: RequestQuoteApiParameter = {
            quoteId: (isAuthenticated ? cartId : undefined) ?? "current",
            productId: rfqBillTo.productId,
            customerFirstName: rfqBillTo.customerFirstName,
            customerLastName: rfqBillTo.customerLastName,
            customerEmailAddress: rfqBillTo.customerEmailAddress,
            customerPhoneNumber: rfqBillTo.customerPhoneNumber,
            customerCompanyName: rfqBillTo.customerCompanyName,
            customerAddressLine1: rfqBillTo.customerAddressLine1,
            customerAddressLine2: rfqBillTo.customerAddressLine2,
            customerCity: rfqBillTo.customerCity,
            customerState: rfqBillTo.customerState,
            customerZip: rfqBillTo.customerZip,
            taxExempt: rfqBillTo.taxExempt,
            taxExemptCertificateNumber: rfqBillTo.taxExempt ? rfqBillTo.taxExemptCertificateNumber : "",
            orderNote: rfqBillTo.orderNote,
            isAuthenticated,
        };

        xngSubmitQuote({
            ...requestQuoteApiParameter,
            onSuccess: () => {
                handleCloseModal();
                toasterContext.addToast({
                    body: siteMessage("Custom_RFQ_Request_Quote_Modal_Success_Message"),
                    messageType: "success",
                });
            },
            onComplete: () => {
                setIsInProgress(false);
            },
        });
    };

    useEffect(() => {
        if (countries?.length) {
            setCountry(countries[0]);
        }
    }, [countries]);

    useEffect(() => {
        if (isModalOpened) {
            xngGetQuote({
                onSuccess: result => {
                    if (isAuthenticated) {
                        setRfqBillTo({ ...result, productId });
                    } else {
                        setRfqBillTo({
                            ...requestQuoteData,
                            productId,
                        });
                    }
                },
                onComplete(resultProps) {
                    if (resultProps.apiResult) {
                        setIsInProgress(false);
                    }
                },
            });
        }
    }, [isModalOpened, requestQuoteData, isAuthenticated, productId]);

    useEffect(() => {
        if (productId) {
            setRfqBillTo(prev => ({
                ...prev,
                productId,
            }));
        }
    }, [productId]);

    return (
        <Modal
            {...styles.modalStyle}
            headline={translate("Request a Quote")}
            isOpen={isModalOpened}
            handleClose={handleCloseModal}
            data-test-selector="rfq_request_quote_modal"
        >
            <StyledForm id={formId} onSubmit={submitFeedbackClickHandler} noValidate>
                <Typography {...styles.requiredInformationText}>* {translate("Required Information")}</Typography>
                <GridContainer {...styles.formGridContainer}>
                    <GridItem {...styles.firstNameGridItem}>
                        <TextField
                            {...styles.firstNameTextField}
                            label={translate("First Name*")}
                            placeholder={translate("First Name")}
                            name="customerFirstName"
                            value={rfqBillTo?.customerFirstName ?? ""}
                            onChange={handleBillToInputChange}
                            required
                            type="firstName"
                            {...{ formId }}
                        />
                    </GridItem>

                    <GridItem {...styles.lastNameGridItem}>
                        <TextField
                            {...styles.lastNameTextField}
                            label={translate("Last Name*")}
                            placeholder={translate("Last Name")}
                            name="customerLastName"
                            value={rfqBillTo?.customerLastName ?? ""}
                            onChange={handleBillToInputChange}
                            required
                            type="lastName"
                            {...{ formId }}
                        />
                    </GridItem>

                    <GridItem {...styles.emailGridItem}>
                        <TextField
                            {...styles.emailTextField}
                            label={translate("Email Address*")}
                            data-test-selector="createAccount_email"
                            placeholder={translate("eg. example@gmail.com")}
                            name="customerEmailAddress"
                            value={rfqBillTo?.customerEmailAddress ?? ""}
                            onChange={handleBillToInputChange}
                            required
                            type="email"
                            {...{ formId }}
                        />
                    </GridItem>

                    <GridItem {...styles.phoneGridItem}>
                        <TextField
                            {...styles.phoneTextField}
                            label={translate("Phone Number")}
                            placeholder="( ___ ) ___ - ____"
                            name="customerPhoneNumber"
                            value={rfqBillTo?.customerPhoneNumber ?? ""}
                            onChange={handleBillToInputChange}
                            type="phone"
                            {...{ formId }}
                        />
                    </GridItem>

                    <GridItem {...styles.companyNameGridItem}>
                        <TextField
                            {...styles.companyNameTextField}
                            label={translate("Company Name*")}
                            placeholder={translate("Company Name")}
                            name="customerCompanyName"
                            value={rfqBillTo?.customerCompanyName ?? ""}
                            onChange={handleBillToInputChange}
                            required
                            type="text"
                            {...{ formId }}
                        />
                    </GridItem>

                    <GridItem {...styles.addressGridItem}>
                        <TextField
                            {...styles.addressTextField}
                            label={translate("Address Line 1*")}
                            placeholder={translate("Address Line 1")}
                            name="customerAddressLine1"
                            value={rfqBillTo?.customerAddressLine1 ?? ""}
                            onChange={handleBillToInputChange}
                            required
                            type="address"
                            {...{ formId }}
                        />
                    </GridItem>

                    <GridItem {...styles.addressGridItem}>
                        <TextField
                            {...styles.addressTextField}
                            label={translate("Address Line 2")}
                            placeholder={translate("Address Line 2")}
                            name="customerAddressLine2"
                            value={rfqBillTo?.customerAddressLine2 ?? ""}
                            onChange={handleBillToInputChange}
                            type="address"
                            required={false}
                            {...{ formId }}
                        />
                    </GridItem>

                    <GridItem {...styles.cityGridItem}>
                        <TextField
                            {...styles.cityTextField}
                            label={translate("City*")}
                            placeholder={translate("City")}
                            name="customerCity"
                            value={rfqBillTo?.customerCity ?? ""}
                            onChange={handleBillToInputChange}
                            required
                            type="city"
                            {...{ formId }}
                        />
                    </GridItem>

                    <GridItem {...styles.stateGridItem}>
                        <Select
                            label={translate("State*")}
                            {...styles.stateSelect}
                            name="customerState"
                            value={rfqBillTo?.customerState ?? ""}
                            onChange={handleBillToInputChange}
                            error={showError && rfqBillTo?.customerState === "" && stateErrorMessage}
                            required
                            {...{ formId }}
                        >
                            <option value="">{translate("Select")}</option>
                            {country?.states?.map(o => (
                                <option key={o.abbreviation} value={o.abbreviation}>
                                    {o.name}
                                </option>
                            ))}
                        </Select>
                    </GridItem>

                    <GridItem {...styles.zipCodeGridItem}>
                        <TextField
                            {...styles.zipCodeTextField}
                            label={translate("Zip Code*")}
                            placeholder={translate("Zip Code")}
                            name="customerZip"
                            value={rfqBillTo?.customerZip ?? ""}
                            onChange={handleBillToInputChange}
                            required
                            type={country?.abbreviation === "US" ? "postalCodeUS" : "postalCodeCA"}
                            {...{ formId }}
                        />
                    </GridItem>
                </GridContainer>

                <StyledWrapper {...styles.enableTaxExemptWrapper}>
                    <StyledWrapper {...styles.enableTaxExemptTextWrapper}>
                        <Typography {...styles.enableTaxExemptText}>{translate("Tax Exempt (Optional)")}</Typography>

                        <Tooltip
                            {...styles.enableTaxExemptTooltip}
                            text={siteMessage("Rfq_RequestQuoteForm_TaxExemptTooltip").toString()}
                        />
                    </StyledWrapper>

                    <RadioGroup
                        {...styles.enableTaxExemptRadioGroup}
                        id="taxExempt"
                        name="taxExempt"
                        value={rfqBillTo?.taxExempt ? "true" : "false"}
                        onChangeHandler={() =>
                            setRfqBillTo(prev => {
                                const taxExempt = !prev.taxExempt;

                                if (!taxExempt) {
                                    xResetFormFieldError(formId, taxExemptCertificateLabel);
                                }

                                return {
                                    ...prev,
                                    taxExempt,
                                    taxExemptCertificateNumber: taxExempt ? "" : prev.taxExemptCertificateNumber,
                                };
                            })
                        }
                    >
                        <Radio {...styles.yesOptionRadio} value="true">
                            {translate("Yes")}
                        </Radio>
                        <Radio {...styles.noOptionRadio} value="false">
                            {translate("No")}
                        </Radio>
                    </RadioGroup>
                    <StyledWrapper {...styles.textExemptTextFieldWrapper} {...{ taxExempt: rfqBillTo?.taxExempt }}>
                        <TextField
                            {...styles.textExemptTextField}
                            name="taxExemptCertificateNumber"
                            label={taxExemptCertificateLabel}
                            placeholder={translate("Certificate Number")}
                            value={rfqBillTo?.taxExemptCertificateNumber ?? ""}
                            onChange={handleBillToInputChange}
                            required={rfqBillTo?.taxExempt}
                            type="taxExempt"
                            {...{ formId }}
                        />
                    </StyledWrapper>
                </StyledWrapper>

                <StyledWrapper {...styles.additionalCommentsWrapper}>
                    <TextArea
                        label={translate("Additional Comments (Optional)")}
                        name="orderNote"
                        value={rfqBillTo?.orderNote ?? ""}
                        onChange={handleBillToInputChange}
                        data-test-selector="rfq_request_quote_modal_additional_comments"
                        {...styles.additionalCommentsTextArea}
                        {...{ formId }}
                    />
                </StyledWrapper>
            </StyledForm>

            <StyledWrapper {...styles.buttonsWrapper}>
                <Button
                    {...styles.cancelButton}
                    type="button"
                    onClick={handleCloseModal}
                    data-test-selector="rfq_request_quote_modal_cancel"
                    aria-label="Cancel"
                >
                    {translate("Cancel")}
                </Button>
                <Button
                    {...styles.submitButton}
                    type="submit"
                    form={formId}
                    disabled={isInProgress}
                    data-test-selector="rfq_request_quote_modal_submit"
                    aria-label={translate("Submit")}
                >
                    {translate("Submit")}
                </Button>
            </StyledWrapper>
        </Modal>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(RFQRequestModal);
